<script setup>
import { reactive, watchEffect, computed, onMounted } from 'vue';
const props = defineProps({
  perPage: { //1ページあたりの表示件数
    type: Number,
    default: 20,
    required: true
  },
  showPages: { //ページネーションを何件表示するか
    type: Number,
    default: 20,
    required: true
  },
  currentPage: { //現在のページ
    type: Number,
    default: 1,
    required: true
  },
  totalCount: { //総件数
    type: Number,
    default: 0,
    required: true
  },
  totalPages: { //総ページ数
    type: Number,
    default: 0,
    required: true
  },
});

const state = reactive({
  currentPageEdited: Number, //現在のページ
  prevAbbreviated: false,
  nextAbbreviated: false
});

const emit = defineEmits(['currentPage']);

//ページネーションを複数設置したときの対応
const currentPage = () => {
  state.currentPageEdited = props.currentPage;
};

watchEffect(() => {
  currentPage();
});

//省略文字の表示コントロール
state.prevAbbreviated = computed(() => {
  const excludeLength = Math.floor(props.showPages / 2);
  if(props.currentPage - excludeLength > excludeLength){
    return true;
  }else{
    return false;
  }
})
state.nextAbbreviated = computed(() => {
  const excludeLength = Math.floor(props.showPages / 2);
  if((props.totalPages - props.currentPage - excludeLength) > 0){
    return true;
  }else{
    return false;
  }
})

//ページ番号を計算する
const numFix = computed(() => {
  return function (num) {
    const adjust = 1 + (props.showPages - 1) / 2;
    let result = num;
    //前ページがマイナスになる場合は1からはじめる
    if (state.currentPageEdited > props.showPages / 2) {
      result = num + state.currentPageEdited - adjust;
    }
    //後ページが最大ページを超える場合は最大ページを超えないようにする
    if (state.currentPageEdited + props.showPages / 2 > props.totalPages) {
      result = props.totalPages - props.showPages + num;
    }
    //総ページ数が表示ページ数に満たない場合、連番そのまま
    if (props.totalPages <= props.showPages) {
      result = num;
    }
    return result;
  };
});

//総記事数が表示ページ数以下の場合に調整する
const showPagesFix = computed(() => {
  if (props.totalPages < props.showPages) {
    return props.totalPages;
  } else {
    return props.showPages;
  }
});

onMounted(() => {
  state.currentPageEdited = props.currentPage;
});

//何ページ目を表示するか
const setPage = (page) => {
  //マイナスにならないようにする
  if (page <= 0) {
    state.currentPageEdited = 1;
  }
  //最大ページを超えないようにする
  else if (page > props.totalPages) {
    state.currentPageEdited = props.totalPages;
  } else {
    state.currentPageEdited = page;
  }
  //親コンポーネントに現在のページを送る
  emit('currentPage', state.currentPageEdited);
};
</script>

<template>
  <nav v-if="props.totalPages > 1" class="p-nav-pager l-gap--xss">
    <div
      class="p-nav-pager__prev-first"
      :class="{ disabled: state.currentPageEdited == 1 }"
    >
      <button
        type="button"
        class="page-link"
        @click.prevent="setPage(1)"
      >
        <i class="c-icon" aria-hidden="true">
          <svg class="svg-icon_arrow-dims">
            <use xlink:href="/cms/wp-content/themes/mizuho-bp/assets/img/shared/sprite.symbol.svg#icon_arrow"></use>
          </svg>
          <svg class="svg-icon_arrow-dims">
            <use xlink:href="/cms/wp-content/themes/mizuho-bp/assets/img/shared/sprite.symbol.svg#icon_arrow"></use>
          </svg>
        </i>
        <span>最初へ</span>
      </button>
    </div>
    <ul>
      <li
        class="p-nav-pager__prev"
        :class="{ disabled: state.currentPageEdited == 1 }"
      >
        <button
          type="button"
          :class="{ disabled: state.currentPageEdited == 1 }"
          @click.prevent="setPage(state.currentPageEdited - 1)"
        >
          <i class="c-icon" aria-hidden="true">
            <svg class="svg-icon_arrow-dims">
              <use xlink:href="/cms/wp-content/themes/mizuho-bp/assets/img/shared/sprite.symbol.svg#icon_arrow"></use>
            </svg>
          </i>
          <span>前へ</span>
        </button>
      </li>
      <li
        v-if="state.prevAbbreviated"
        class="page-item"
      >
        <span class="page-link">…</span>
      </li>
      <li
        v-for="num in showPagesFix"
        :key="num"
        class="page-item"
        :class="{ current: numFix(num) == state.currentPageEdited }"
      >
        <template v-if="numFix(num) == state.currentPageEdited">
          <span class="page-link">{{ numFix(num) }}</span>
        </template>
        <button
          v-else
          type="button"
          @click.prevent="setPage(numFix(num))"
        >
          {{ numFix(num) }}
        </button>
      </li>
      <li
        v-if="state.nextAbbreviated"
        class="page-item"
      >
        <span class="page-link">…</span>
      </li>
      <li
        class="p-nav-pager__next"
        :class="{ disabled: state.currentPageEdited == props.totalPages }"
      >
        <button
          type="button"
          @click.prevent="setPage(state.currentPageEdited + 1)"
        >
          <span>次へ</span>
          <i class="c-icon" aria-hidden="true">
            <svg class="svg-icon_arrow-dims">
              <use xlink:href="/cms/wp-content/themes/mizuho-bp/assets/img/shared/sprite.symbol.svg#icon_arrow"></use>
            </svg>
          </i>
        </button>
      </li>
    </ul>
    <div
      class="p-nav-pager__next-last"
      :class="{ disabled: state.currentPageEdited == props.totalPages }"
    >
      <button
        type="button"
        @click.prevent="setPage(props.totalPages)"
      >
        <span>最後へ</span>
        <i class="c-icon" aria-hidden="true">
          <svg class="svg-icon_arrow-dims">
            <use xlink:href="/cms/wp-content/themes/mizuho-bp/assets/img/shared/sprite.symbol.svg#icon_arrow"></use>
          </svg>
          <svg class="svg-icon_arrow-dims">
            <use xlink:href="/cms/wp-content/themes/mizuho-bp/assets/img/shared/sprite.symbol.svg#icon_arrow"></use>
          </svg>
        </i>
      </button>
    </div>
  </nav>
</template>
